import React, { useState, useEffect } from 'react';
import {
  adminListUsers,
  adminGetUser,
  adminUpdateUserTier,
  adminToggleStaff,
  adminListTiers
} from '../../services/api';

function UserManagementTable() {
  const [users, setUsers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersData, tiersData] = await Promise.all([
        adminListUsers(),
        adminListTiers()
      ]);
      // API returns {users: [...]} and {tiers: [...]} so extract the arrays
      setUsers(usersData.users || usersData);
      setTiers(tiersData.tiers || tiersData);
    } catch (err) {
      setError('Failed to load users. ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Re-read one user from the backend so App access (which depends on tier,
  // admin status and the Feature Matrix) always matches the server.
  const refreshUserRow = async (userId) => {
    try {
      const fresh = await adminGetUser(userId);
      setUsers(prev => prev.map(user => (user.id === userId ? fresh : user)));
    } catch (err) {
      // Keep the optimistic row; the Refresh button reloads everything
    }
  };

  const handleTierChange = async (userId, newTier) => {
    setUpdating(prev => ({ ...prev, [`tier-${userId}`]: true }));
    try {
      await adminUpdateUserTier(userId, newTier);
      setUsers(prev => prev.map(user =>
        user.id === userId
          ? { ...user, tier: newTier, tier_display: tiers.find(t => t.name === newTier)?.display_name || newTier }
          : user
      ));
      await refreshUserRow(userId);
    } catch (err) {
      setError('Failed to update tier: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUpdating(prev => ({ ...prev, [`tier-${userId}`]: false }));
    }
  };

  const handleStaffToggle = async (userId, currentIsStaff) => {
    setUpdating(prev => ({ ...prev, [`staff-${userId}`]: true }));
    try {
      await adminToggleStaff(userId, !currentIsStaff);
      setUsers(prev => prev.map(user =>
        user.id === userId
          ? {
              ...user,
              is_staff: !currentIsStaff,
              effective_tier: !currentIsStaff ? 'admin' : user.tier
            }
          : user
      ));
      await refreshUserRow(userId);
    } catch (err) {
      // The backend refuses self-demotion and demoting the last admin, with a reason in detail
      setError(err.response?.data?.detail || ('Failed to toggle admin status: ' + err.message));
    } finally {
      setUpdating(prev => ({ ...prev, [`staff-${userId}`]: false }));
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Who sees what: Admin = everything; Yes = the app; Paused = only the offline notice
  const getAppAccess = (user) => {
    if (user.is_staff) return { label: 'Admin', className: 'tier-badge tier-admin' };
    if (user.has_app_access) return { label: 'Yes', className: 'status-indicator active' };
    return { label: 'Paused', className: 'status-indicator inactive' };
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case 'admin': return 'tier-badge tier-admin';
      case 'premium': return 'tier-badge tier-premium';
      case 'plus': return 'tier-badge tier-plus';
      case 'essential': return 'tier-badge tier-essential';
      default: return 'tier-badge tier-free';
    }
  };

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="user-management-table">
      <div className="table-header">
        <h3>User Management</h3>
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by username or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError('')} className="dismiss-btn">Dismiss</button>
        </div>
      )}

      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Subscription Tier</th>
              <th>Effective Tier</th>
              <th title="Admin always has full access. Otherwise set by the tier's App Access switch in Feature Matrix.">App access</th>
              <th>Admin</th>
              <th>Active</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-results">
                  {searchTerm ? 'No users match your search.' : 'No users found.'}
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id} className={user.is_staff ? 'admin-row' : ''}>
                  <td className="username-cell">
                    {user.username}
                  </td>
                  <td className="email-cell">{user.email || '-'}</td>
                  <td className="tier-cell">
                    <select
                      value={user.tier}
                      onChange={(e) => handleTierChange(user.id, e.target.value)}
                      disabled={updating[`tier-${user.id}`]}
                      className="tier-select"
                    >
                      {tiers.map(tier => (
                        <option key={tier.id} value={tier.name}>
                          {tier.display_name}
                        </option>
                      ))}
                    </select>
                    {updating[`tier-${user.id}`] && <span className="updating-indicator">...</span>}
                  </td>
                  <td className="effective-tier-cell">
                    <span className={getTierBadgeClass(user.effective_tier)}>
                      {user.effective_tier === 'admin' ? 'Admin' : user.tier_display}
                    </span>
                  </td>
                  <td className="app-access-cell">
                    <span className={getAppAccess(user).className}>
                      {getAppAccess(user).label}
                    </span>
                  </td>
                  <td className="admin-cell">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={user.is_staff}
                        onChange={() => handleStaffToggle(user.id, user.is_staff)}
                        disabled={updating[`staff-${user.id}`]}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    {updating[`staff-${user.id}`] && <span className="updating-indicator">...</span>}
                  </td>
                  <td className="active-cell">
                    <span className={`status-indicator ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="date-cell">
                    {user.date_joined ? new Date(user.date_joined).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <span className="user-count">
          Showing {filteredUsers.length} of {users.length} users
        </span>
        <button onClick={loadData} className="refresh-btn" disabled={loading}>
          Refresh
        </button>
      </div>
    </div>
  );
}

export default UserManagementTable;
